import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { getIcon } from '@/utils/icon-utils';
import type { LucideIcon } from 'lucide-react';

/**
 * Content interface for Button component
 * Follows content-props pattern for complete UI/content separation
 */
export interface ButtonContent {
  /**
   * Button text content
   */
  text: string;

  /**
   * Optional icon - can be a Lucide icon component or icon name string
   * Icons are always displayed on the right side of the text
   */
  icon?: string | LucideIcon;

  /**
   * Optional aria-label for accessibility (overrides default text)
   */
  ariaLabel?: string;
}

/**
 * Button variant styles using CSS variables from OH Design System
 * All colors reference CSS variables - no hardcoded hex values
 *
 * Includes both OH Design System variants (primary, secondary) and
 * legacy variants for backward compatibility.
 */
const buttonVariants = cva(
  // Base styles - common to all buttons
  [
    'inline-flex items-center justify-center',
    'font-semibold',
    'whitespace-nowrap',
    'ring-offset-background',
    'cursor-pointer',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    // Transition with reduced motion support
    'transition-colors duration-150',
    'motion-reduce:transition-none',
  ],
  {
    variants: {
      variant: {
        // OH Design System Primary Variant
        primary: [
          'bg-button-primary text-white border border-transparent',
          'hover:bg-button-primary-hover',
          'disabled:bg-button-primary-disabled disabled:text-button-primary-disabled-foreground',
        ],
        // OH Design System Secondary Variant
        secondary: [
          'bg-background text-button-primary border border-button-primary',
          'hover:bg-button-secondary-hover hover:text-button-primary-hover hover:border-button-primary-hover',
          'disabled:bg-button-secondary-disabled disabled:text-button-secondary-disabled-foreground disabled:border-button-secondary-disabled-border',
        ],
        // Legacy variants for backward compatibility
        'oh-primary': [
          'rounded-[4px] bg-button-primary text-white border border-transparent',
          'hover:bg-button-primary-hover',
          'disabled:bg-button-primary-disabled disabled:text-button-primary-disabled-foreground',
        ],
        'oh-secondary': [
          'rounded-[4px] bg-background text-button-primary border border-button-primary',
          'hover:bg-button-secondary-hover hover:text-button-primary-hover hover:border-button-primary-hover',
          'disabled:bg-button-secondary-disabled disabled:text-button-secondary-disabled-foreground disabled:border-button-secondary-disabled-border',
        ],
        'oh-icon-only': [
          'rounded-[4px] bg-background text-button-primary border border-button-primary',
          'hover:bg-button-secondary-hover hover:text-button-primary-hover hover:border-button-primary-hover',
        ],
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        black: 'bg-button-black text-button-black-foreground shadow-sm hover:bg-button-black/90',
        revenue: 'bg-revenue-primary text-revenue-primary-foreground hover:bg-revenue-primary/90',
        'revenue-secondary': 'bg-revenue-secondary text-revenue-secondary-foreground hover:bg-revenue-secondary/90',
        'revenue-outline': 'border border-revenue-primary text-revenue-primary bg-background hover:bg-revenue-primary/10',
      },
      size: {
        // OH Design System sizes (base dimensions, padding applied via compound variants)
        small: 'h-8 min-w-[140px] text-base leading-6 rounded-[4px]',
        default: 'h-12 min-w-[140px] text-base leading-6 rounded-[4px]',
        medium: 'h-10 min-w-[140px] text-base leading-6 rounded-[4px]',
        large: 'h-14 min-w-[140px] text-xl leading-7 rounded-[4px]',
        // Legacy sizes
        sm: 'h-8 px-4 text-sm rounded-lg gap-2',
        lg: 'h-14 px-10 text-lg rounded-lg gap-2',
        icon: 'h-10 w-10 rounded-lg',
        // OH Design System sizes - Primary with icon
        'oh-default': 'h-12 w-[140px] text-base leading-6',
        'oh-small': 'h-8 w-[140px] text-base leading-6',
        'oh-medium': 'h-10 w-[140px] text-base leading-6',
        'oh-large': 'h-14 w-[140px] text-xl leading-7',
        'oh-default-icon': 'h-12 w-[67px] text-base leading-6',
        'oh-small-icon': 'h-8 w-[75px] text-base leading-6',
        'oh-medium-icon': 'h-10 w-[67px] text-base leading-6',
        'oh-large-icon': 'h-14 w-[88px] text-xl leading-7',
        // OH Design System sizes - Secondary with icon
        'oh-default-icon-secondary': 'h-12 min-w-[198px] px-4 text-base leading-6',
        'oh-small-icon-secondary': 'h-8 min-w-[181px] px-3 text-base leading-6',
        'oh-medium-icon-secondary': 'h-10 min-w-[189px] px-4 text-base leading-6',
        'oh-large-icon-secondary': 'h-14 min-w-[236px] px-5 text-xl leading-7',
        // OH Design System sizes - Icon only
        'oh-icon-only': 'h-12 w-12 p-4',
      },
      hasIcon: {
        true: '',
        false: '',
      },
      iconOnly: {
        true: 'w-12 h-12 p-4 rounded-[4px]',
        false: '',
      },
    },
    compoundVariants: [
      // Text-only buttons - padding and gap per spec
      { hasIcon: false, iconOnly: false, size: 'small', className: 'px-6 gap-2' },
      { hasIcon: false, iconOnly: false, size: 'default', className: 'gap-2.5' },
      { hasIcon: false, iconOnly: false, size: 'medium', className: 'px-4 gap-2' },
      { hasIcon: false, iconOnly: false, size: 'large', className: 'px-6 gap-2' },
      // Icon buttons with text - flexible width, 16px padding and 6px radius
      { hasIcon: true, size: 'small', className: 'w-auto px-4 gap-2 rounded-[6px]' },
      { hasIcon: true, size: 'default', className: 'w-auto px-4 gap-2 rounded-[6px]' },
      { hasIcon: true, size: 'medium', className: 'w-auto px-4 gap-2 rounded-[6px]' },
      { hasIcon: true, size: 'large', className: 'w-auto px-4 gap-2 rounded-[6px]' },
      // Icon-only overrides all size settings
      { iconOnly: true, className: 'w-12 h-12 p-4 rounded-[4px] gap-2.5' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      hasIcon: false,
      iconOnly: false,
    },
  }
);

/**
 * Icon size classes based on button size
 */
const iconSizeClasses = {
  small: 'h-4 w-4',
  default: 'h-6 w-6',
  medium: 'h-6 w-6',
  large: 'h-6 w-6',
  sm: 'h-4 w-4',
  lg: 'h-6 w-6',
  icon: 'h-5 w-5',
  'oh-default': 'h-6 w-6',
  'oh-small': 'h-4 w-4',
  'oh-medium': 'h-6 w-6',
  'oh-large': 'h-6 w-6',
  'oh-default-icon': 'h-6 w-6',
  'oh-small-icon': 'h-4 w-4',
  'oh-medium-icon': 'h-6 w-6',
  'oh-large-icon': 'h-6 w-6',
  'oh-default-icon-secondary': 'h-6 w-6',
  'oh-small-icon-secondary': 'h-4 w-4',
  'oh-medium-icon-secondary': 'h-6 w-6',
  'oh-large-icon-secondary': 'h-6 w-6',
  'oh-icon-only': 'h-6 w-6',
} as const;

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'content'>,
    Omit<VariantProps<typeof buttonVariants>, 'hasIcon' | 'iconOnly'> {
  /**
   * Content object containing button text and optional icon
   * For icon-only buttons, provide empty string for text and set iconOnly to true
   */
  content?: ButtonContent;

  /**
   * Whether this is an icon-only button (no text)
   * @default false
   */
  iconOnly?: boolean;

  /**
   * Render as child component (for Link wrappers)
   * Uses Radix Slot for polymorphic rendering
   * @default false
   */
  asChild?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Children for asChild mode or custom content (backward compatibility)
   */
  children?: ReactNode;
}

/**
 * Button - OH Design System button component
 *
 * A fully accessible button component with support for:
 * - Primary and secondary variants
 * - Four sizes (small, default, medium, large)
 * - Icon support (with text or icon-only)
 * - Disabled states with proper styling
 * - Polymorphic rendering via asChild
 *
 * @example
 * ```tsx
 * // Primary button with text
 * <Button content={{ text: 'Submit' }} />
 *
 * // Secondary button with icon
 * <Button
 *   variant="secondary"
 *   content={{ text: 'Add Item', icon: 'Plus' }}
 * />
 *
 * // Icon-only button
 * <Button
 *   iconOnly
 *   content={{ text: '', icon: 'Search', ariaLabel: 'Search' }}
 * />
 *
 * // With children (backward compatibility)
 * <Button>Click me</Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      content,
      variant = 'primary',
      size = 'default',
      iconOnly = false,
      asChild = false,
      className,
      children,
      type = 'button',
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Determine if we have an icon from content prop (not children)
    const hasIcon = Boolean(content?.icon) && !iconOnly;

    // Get the icon component if provided via content prop
    const IconComponent = content?.icon
      ? typeof content.icon === 'string'
        ? getIcon(content.icon)
        : content.icon
      : null;

    // Get appropriate icon size class
    const iconSizeClass = iconSizeClasses[size || 'default'];

    // Determine aria-label
    const ariaLabel =
      content?.ariaLabel ||
      (iconOnly && content?.icon
        ? typeof content.icon === 'string'
          ? content.icon
          : 'button'
        : undefined);

    // Render content based on mode
    const renderContent = () => {
      // If children are provided (backward compatibility), use them
      if (children) {
        return children;
      }

      // If no content, return null (component handles gracefully)
      if (!content) {
        return null;
      }

      // Icon-only mode
      if (iconOnly) {
        if (!IconComponent) return null;
        return <IconComponent className={iconSizeClass} aria-hidden="true" />;
      }

      // Text with optional icon (icon on right)
      return (
        <>
          {content.text && <span>{content.text}</span>}
          {IconComponent && (
            <IconComponent className={iconSizeClass} aria-hidden="true" />
          )}
        </>
      );
    };

    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        className={cn(
          buttonVariants({
            variant,
            size,
            hasIcon,
            iconOnly,
          }),
          className
        )}
        {...props}
      >
        {renderContent()}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

// Export variant function for external use if needed
export { buttonVariants };
